/**
 * Global app state + minimal subscription layer. Two-file state machine:
 * EMPTY → HAS_A → HAS_BOTH, reset back to EMPTY.
 *
 * No framework. Listeners get a fresh snapshot on every transition; they
 * decide what to re-render.
 */

import type { FLPProject } from "flpdiff";
import { parseFLPFile } from "flpdiff";

export interface LoadedFile {
  readonly name: string;
  readonly bytes: number;
  readonly project: FLPProject;
}

export interface ParseError {
  readonly name: string;
  readonly message: string;
}

export type Phase = "EMPTY" | "HAS_A" | "HAS_BOTH";

export interface State {
  readonly phase: Phase;
  readonly a: LoadedFile | null;
  readonly b: LoadedFile | null;
  readonly error: ParseError | null;
}

type Listener = (state: State) => void;

let state: State = { phase: "EMPTY", a: null, b: null, error: null };
const listeners = new Set<Listener>();

export function getState(): State {
  return state;
}

export function subscribe(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function emit(next: State): void {
  state = next;
  for (const fn of listeners) fn(state);
}

/** Sort-then-assign drop of 1 or 2 files. Fills empty slots in order. */
export async function addFiles(files: readonly File[]): Promise<void> {
  const accepted = files.filter((f) => f.name.toLowerCase().endsWith(".flp"));
  const rejected = files.length - accepted.length;

  if (accepted.length === 0) {
    emit({
      ...state,
      error: { name: files[0]?.name ?? "?", message: "Only .flp files are supported" },
    });
    return;
  }

  // In HAS_BOTH, ignore drops — user must reset first.
  if (state.phase === "HAS_BOTH") {
    emit({ ...state, error: { name: accepted[0]!.name, message: "Reset to compare different files" } });
    return;
  }

  try {
    const first = await loadOne(accepted[0]!);
    let a = state.a;
    let b = state.b;
    if (!a) a = first;
    else if (!b) b = first;

    if (accepted.length >= 2 && !b) {
      b = await loadOne(accepted[1]!);
    }

    const phase: Phase = a && b ? "HAS_BOTH" : a ? "HAS_A" : "EMPTY";
    emit({
      phase,
      a,
      b,
      error: rejected > 0
        ? { name: "", message: `Skipped ${rejected} non-.flp file${rejected === 1 ? "" : "s"}` }
        : null,
    });
  } catch (e) {
    emit({
      ...state,
      error: {
        name: accepted[0]!.name,
        message: e instanceof Error ? e.message : String(e),
      },
    });
  }
}

async function loadOne(file: File): Promise<LoadedFile> {
  const buf = await file.arrayBuffer();
  const project = parseFLPFile(buf);
  return { name: file.name, bytes: file.size, project };
}

export function reset(): void {
  emit({ phase: "EMPTY", a: null, b: null, error: null });
}

export function swap(): void {
  if (state.phase !== "HAS_BOTH" || !state.a || !state.b) return;
  emit({ ...state, a: state.b, b: state.a });
}

export function removeA(): void {
  if (!state.a) return;
  // Promote B → A if present so the user doesn't lose their work.
  const next: State = state.b
    ? { phase: "HAS_A", a: state.b, b: null, error: null }
    : { phase: "EMPTY", a: null, b: null, error: null };
  emit(next);
}

export function removeB(): void {
  if (!state.b) return;
  emit({ phase: state.a ? "HAS_A" : "EMPTY", a: state.a, b: null, error: null });
}

export function clearError(): void {
  if (state.error === null) return;
  emit({ ...state, error: null });
}

/** Inject a pre-loaded pair (used by demo routing). */
export function setPair(a: LoadedFile, b: LoadedFile): void {
  emit({ phase: "HAS_BOTH", a, b, error: null });
}
